import React from 'react'
import styles from './BlazeTable.module.scss'
import cx from 'classnames'
import { SortingDirection } from 'api/clients/FilesViewsClient'
import { ArrowDown } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

type TableComponentProps = {
  children: React.ReactNode
}

type TableHeaderProps = TableComponentProps & {
  isSticky?: boolean
}

type TableProps = TableComponentProps & {
  maxWidth?: string
  variant?: TableVariant
}

type CellProps = TableComponentProps & {
  maxWidth?: string
  className?: string
}

type TableRowProps = TableComponentProps & {
  onClick?: () => void
  className?: string
  selected?: boolean
  selectable?: boolean
  withoutBorder?: boolean
}

type HeaderCellProps = CellProps & {
  onClick?: () => void
  sortDirection?: SortingDirection | null
}

export type TableVariant = 'default' | 'light'

export const Table: React.FC<TableProps> & {
  Thead: typeof Thead
  Tbody: typeof Tbody
  Tr: typeof Tr
  Td: typeof Td
  Th: typeof Th
} = ({ children, maxWidth, variant = 'default' }) => {
  return (
    <table className={cx(styles.table, styles[variant])} style={{ maxWidth: maxWidth }} data-testid="blaze-table">
      {children}
    </table>
  )
}

// Table Head
const Thead: React.FC<TableHeaderProps> = ({ children, isSticky }) => {
  return <thead className={cx(styles.thead, { [styles.sticky]: isSticky })}>{children}</thead>
}

// Table Body
const Tbody: React.FC<TableComponentProps> = ({ children }) => {
  return <tbody className={styles.tbody}>{children}</tbody>
}

// Table Row
const Tr: React.FC<TableRowProps> = ({ children, onClick, className, selected, selectable, withoutBorder = false }) => {
  return (
    <tr
      className={cx(styles.tr, className, {
        [styles.clickable]: onClick,
        [styles.selected]: selected,
        [styles.selectable]: selectable,
        [styles.withoutBorder]: withoutBorder,
      })}
      onClick={onClick}
    >
      {children}
    </tr>
  )
}

// Table Data Cell
const Td: React.FC<CellProps> = ({ children, maxWidth, className }) => {
  const cellStyle = maxWidth ? { maxWidth, className: styles.fixedWidth } : {}
  return (
    <td
      className={cx(styles.td, cellStyle.className, className)}
      style={{ maxWidth: cellStyle.maxWidth, minWidth: cellStyle.maxWidth }}
    >
      {children}
    </td>
  )
}

// Table Header Cell
const Th: React.FC<HeaderCellProps> = ({ children, maxWidth, onClick, sortDirection, className }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const textVariant = isSystemRedesignEnabled ? 'label' : 'smallList'
  const activeTextColor = isSystemRedesignEnabled ? 'var(--dark-40)' : 'var(--dark-80)'

  const cellStyle = maxWidth ? { maxWidth, className: styles.fixedWidth } : {}
  const isActive = !!sortDirection
  return (
    <th
      className={cx(styles.th, { [styles.clickable]: onClick }, cellStyle.className, className)}
      onClick={onClick}
      style={{ maxWidth: cellStyle.maxWidth, minWidth: cellStyle.maxWidth }}
    >
      <div className={cx(styles.thContent)}>
        <Text variant={textVariant} color={isActive ? activeTextColor : 'var(--dark-40)'}>
          {children}
        </Text>
        {isActive && (
          <div className={styles.sortIcon} data-testid="sort-icon" data-dir={sortDirection}>
            <ArrowDown size={isSystemRedesignEnabled ? 12 : 16} color={activeTextColor} />
          </div>
        )}
      </div>
    </th>
  )
}

Table.Thead = Thead
Table.Tbody = Tbody
Table.Tr = Tr
Table.Td = Td
Table.Th = Th

export default Table
